/**
 * Declarative config for the six contractor compliance registers. Each
 * drives a generic RegisterTab: `columns` render the table, `fields`
 * build the drawer form. Field types: text, textarea, number, select,
 * date, switch, file, worker. Column types: text, badge, date, expiry,
 * currency, bool, worker, attachment.
 */

export const INSURANCE_TYPES = [
  { value: "public_liability", label: "Public Liability" },
  { value: "workers_compensation", label: "Workers Compensation" },
  { value: "professional_indemnity", label: "Professional Indemnity" },
  { value: "product_liability", label: "Product Liability" },
  { value: "motor_vehicle", label: "Motor Vehicle" },
  { value: "plant_equipment", label: "Plant & Equipment" },
  { value: "contract_works", label: "Contract Works" },
  { value: "marine_transit", label: "Marine / Transit" },
  { value: "management_liability", label: "Management Liability" },
  { value: "cyber", label: "Cyber" },
  { value: "personal_accident", label: "Personal Accident" },
  { value: "other", label: "Other" },
];

const EMPLOYMENT_TYPES = [
  { value: "employee", label: "Employee" },
  { value: "subcontractor", label: "Subcontractor" },
  { value: "labour_hire", label: "Labour Hire" },
  { value: "apprentice", label: "Apprentice" },
  { value: "other", label: "Other" },
];

/**
 * Site Access is the only worker status that acts. Three values, because it
 * answers one question: may this person be on site and use AirAgri here?
 *
 * Saving Approved grants their AirAgri access — the same action as accepting a
 * contractor user. Saving Pending or Rejected takes it away, so the column can
 * never claim someone is Pending while they can still sign in. Rejected is a
 * deliberate no and holds: they cannot be granted access until it is changed.
 */
export const SITE_ACCESS_STATUSES = [
  { value: "pending", label: "Pending" },
  { value: "approved", label: "Approved" },
  { value: "rejected", label: "Rejected" },
];

const WORKER_STATUSES = [
  { value: "pending", label: "Pending" },
  { value: "verified", label: "Verified" },
  { value: "complete", label: "Complete" },
  { value: "conditional", label: "Conditional" },
  { value: "approved", label: "Approved" },
  { value: "expired", label: "Expired" },
  { value: "rejected", label: "Rejected" },
];

const ASSET_TYPES = [
  "Utility Vehicle", "Truck", "Tractor", "Excavator", "Loader",
  "Elevated Work Platform", "Telehandler", "Forklift", "Trailer",
  "Spray Rig", "Harvester", "Generator", "Compressor", "Chainsaw",
  "Quad / ATV", "Side-by-Side", "Grader", "Water Cart", "Crane", "Other",
].map((v) => ({ value: v, label: v }));

const INSPECTION_STATUSES = [
  { value: "pending", label: "Pending" },
  { value: "current", label: "Current" },
  { value: "overdue", label: "Overdue" },
  { value: "failed", label: "Failed" },
];

const ASSET_APPROVAL_STATUSES = [
  { value: "pending", label: "Pending" },
  { value: "approved", label: "Approved" },
  { value: "conditional", label: "Conditional" },
  { value: "rejected", label: "Rejected" },
];

const SAFETY_FEATURE_STATES = [
  { value: "compliant", label: "Compliant" },
  { value: "non_compliant", label: "Non-compliant" },
  { value: "na", label: "N/A" },
];

const JURISDICTIONS = ["QLD", "NSW", "VIC", "SA", "WA", "TAS", "NT", "ACT", "National"].map(
  (v) => ({ value: v, label: v })
);

const SAFETY_DOC_TYPES = [
  "WHS Policy", "SWMS", "Site-Specific Risk Assessment", "Emergency Response Plan",
  "Isolation/Lockout Procedure", "Plant Risk Assessment", "Chemical Management Plan",
  "Traffic Management Plan", "Environmental Management Plan", "Training Records",
  "Toolbox Talk Records", "Incident Reporting Procedure", "First Aid Procedure",
  "PPE Policy", "Fatigue Management Policy", "Drug & Alcohol Policy", "other",
].map((v) => ({ value: v, label: v === "other" ? "Other" : v }));

export const REGISTER_CONFIGS = {
  insurance: {
    key: "insurance",
    endpoint: "contractor-insurance",
    title: "Insurance Register",
    addLabel: "Add Policy",
    empty: "No insurance policies recorded yet.",
    columns: [
      { key: "insurance_type", label: "Type", type: "option", options: INSURANCE_TYPES },
      { key: "insurer", label: "Insurer", type: "text" },
      { key: "policy_number", label: "Policy Number", type: "text" },
      { key: "coverage_amount", label: "Coverage", type: "currency" },
      { key: "expiry_date", label: "Expiry", type: "expiry" },
      { key: "verified", label: "Verified", type: "bool" },
    ],
    fields: [
      { name: "insurance_type", label: "Insurance Type", type: "select", options: INSURANCE_TYPES, required: true },
      { name: "insurer", label: "Insurer", type: "text", required: true },
      { name: "policyholder", label: "Policyholder", type: "text" },
      { name: "policy_number", label: "Policy Number", type: "text" },
      { name: "coverage_amount", label: "Coverage Amount ($)", type: "number" },
      { name: "geographic_coverage", label: "Geographic Coverage", type: "text" },
      { name: "start_date", label: "Policy Start", type: "date" },
      { name: "expiry_date", label: "Policy Expiry", type: "date" },
      { name: "activities_covered", label: "Activities Covered", type: "textarea" },
      { name: "exclusions", label: "Exclusions", type: "textarea" },
      { name: "verified", label: "Verified", type: "switch" },
      { name: "document_attachment_id", label: "Certificate of Currency", type: "file" },
      { name: "notes", label: "Internal Notes", type: "textarea" },
    ],
  },

  personnel: {
    key: "personnel",
    endpoint: "contractor-worker",
    title: "Worker Register",
    addLabel: "Add Worker",
    empty: "No workers registered yet.",
    columns: [
      { key: "full_name", label: "Worker", type: "text", sub: "role" },
      { key: "employment_type", label: "Employment", type: "option", options: EMPLOYMENT_TYPES },
      { key: "induction_status", label: "Induction", type: "status" },
      { key: "competency_status", label: "Competency", type: "status" },
      // ONE column. Site Access used to be a hand-set compliance value sitting
      // next to a separate derived AirAgri Access, which asked people to hold two
      // similar-sounding statuses in their head and let them disagree. Now there is
      // a single question — may this person be on site and use AirAgri here? —
      // with three answers, and Approved is kept true by granting the access it
      // claims.
      { key: "site_access", label: "Site Access", type: "siteAccess" },
      { key: "last_on_site", label: "Last On Site", type: "date" },
    ],
    fields: [
      { name: "full_name", label: "Full Legal Name", type: "text", required: true },
      { name: "preferred_name", label: "Preferred Name", type: "text" },
      { name: "mobile", label: "Mobile Number", type: "text" },
      { name: "email", label: "Email Address", type: "text" },
      { name: "employment_type", label: "Employment Type", type: "select", options: EMPLOYMENT_TYPES },
      { name: "role", label: "Role / Occupation", type: "text" },
      { name: "employer", label: "Employer", type: "text" },
      { name: "emergency_contact_name", label: "Emergency Contact Name", type: "text" },
      { name: "emergency_contact_relationship", label: "Relationship", type: "text" },
      { name: "emergency_contact_phone", label: "Emergency Contact Phone", type: "text" },
      { name: "approved_activities", label: "Approved Activities", type: "textarea" },
      { name: "lone_worker", label: "Lone Worker", type: "switch" },
      { name: "induction_status", label: "Induction Status", type: "select", options: WORKER_STATUSES },
      { name: "competency_status", label: "Competency Status", type: "select", options: WORKER_STATUSES },
      // Saving this DOES something, unlike the two compliance selects above:
      // Approved grants the person AirAgri access for this property, anything
      // else takes it away. See SITE_ACCESS_STATUSES.
      {
        name: "access_status",
        // Shows the DERIVED state, saves to the stored column. The two can differ
        // when access was granted somewhere that does not write this column, and
        // the drawer must agree with the table.
        valueKey: "site_access",
        label: "Site Access Status",
        type: "select",
        options: SITE_ACCESS_STATUSES,
      },
      { name: "last_on_site", label: "Last On Site", type: "date" },
    ],
  },

  assets: {
    key: "assets",
    endpoint: "contractor-asset",
    title: "Plant, Vehicles & Equipment",
    addLabel: "Add Asset",
    empty: "No assets recorded yet.",
    columns: [
      { key: "make", label: "Asset", type: "text", sub: "asset_type", joinSub: "model" },
      { key: "registration_or_serial", label: "Rego / Serial", type: "text" },
      { key: "inspection_status", label: "Inspection", type: "status" },
      { key: "next_service_date", label: "Next Service", type: "expiry", expiryKey: "service_status" },
      { key: "defects", label: "Defects", type: "text" },
      { key: "approval_status", label: "Approval", type: "status" },
    ],
    fields: [
      { name: "asset_type", label: "Asset Type", type: "select", options: ASSET_TYPES, required: true },
      { name: "make", label: "Make", type: "text" },
      { name: "model", label: "Model", type: "text" },
      { name: "year_manufactured", label: "Year Manufactured", type: "number" },
      { name: "registration_or_serial", label: "Registration or Serial Number", type: "text" },
      { name: "owner", label: "Owner", type: "text" },
      { name: "operator_requirements", label: "Operator Requirements", type: "textarea" },
      { name: "last_service_date", label: "Last Service Date", type: "date" },
      { name: "next_service_date", label: "Next Service Due", type: "date" },
      { name: "rops_fops_status", label: "ROPS / FOPS", type: "select", options: SAFETY_FEATURE_STATES },
      { name: "guarding_status", label: "Guarding", type: "select", options: SAFETY_FEATURE_STATES },
      { name: "seatbelt_status", label: "Seatbelt", type: "select", options: SAFETY_FEATURE_STATES },
      { name: "fire_extinguisher", label: "Fire Extinguisher", type: "select", options: SAFETY_FEATURE_STATES },
      { name: "biosecurity_cleandown_required", label: "Biosecurity Clean-Down Required", type: "switch" },
      { name: "gps_fitted", label: "GPS / Tracker Fitted", type: "switch" },
      { name: "inspection_status", label: "Inspection Status", type: "select", options: INSPECTION_STATUSES },
      { name: "defects", label: "Defects", type: "text" },
      { name: "approval_status", label: "Approval Status", type: "select", options: ASSET_APPROVAL_STATUSES },
      { name: "rego_attachment_id", label: "Registration Certificate", type: "file" },
      { name: "prestart_attachment_id", label: "Pre-Start Checklist", type: "file" },
      { name: "risk_assessment_attachment_id", label: "Plant Risk Assessment", type: "file" },
      { name: "notes", label: "Internal Notes", type: "textarea" },
    ],
  },

  licences: {
    key: "licences",
    endpoint: "contractor-licence",
    title: "Licences, Qualifications & Competencies",
    addLabel: "Add Credential",
    empty: "No credentials recorded yet.",
    columns: [
      { key: "credential_type", label: "Credential", type: "text" },
      { key: "worker_name", label: "Holder", type: "worker" },
      { key: "licence_number", label: "Number", type: "text" },
      { key: "issuing_authority", label: "Issuing Authority", type: "text" },
      { key: "expiry_date", label: "Expiry", type: "expiry", noExpiryKey: "no_expiry" },
      { key: "verified", label: "Verified", type: "bool" },
    ],
    fields: [
      { name: "credential_type", label: "Credential Type", type: "text", required: true },
      { name: "worker_id", label: "Holder (worker)", type: "worker" },
      { name: "licence_number", label: "Licence / Certificate Number", type: "text" },
      { name: "class_or_endorsement", label: "Class or Endorsement", type: "text" },
      { name: "issuing_authority", label: "Issuing Authority", type: "text" },
      { name: "jurisdiction", label: "Jurisdiction", type: "select", options: JURISDICTIONS },
      { name: "issue_date", label: "Issue Date", type: "date" },
      { name: "expiry_date", label: "Expiry Date", type: "date" },
      { name: "no_expiry", label: "This credential has no expiry date", type: "switch" },
      { name: "restrictions", label: "Restrictions or Conditions", type: "textarea" },
      { name: "verified", label: "Verified", type: "switch" },
      { name: "front_attachment_id", label: "Front of Document", type: "file" },
      { name: "back_attachment_id", label: "Back of Document", type: "file" },
      { name: "notes", label: "Internal Notes", type: "textarea" },
    ],
  },

  inductions: {
    key: "inductions",
    endpoint: "contractor-induction",
    title: "Induction Records",
    addLabel: "Record Induction",
    empty: "No induction records yet.",
    columns: [
      { key: "induction_type", label: "Induction", type: "text" },
      { key: "worker_name", label: "Worker", type: "worker" },
      { key: "version", label: "Version", type: "text" },
      { key: "completion_date", label: "Completed", type: "date" },
      { key: "expiry_date", label: "Expiry", type: "expiry" },
      { key: "signature_collected", label: "Signed", type: "bool" },
      // Where the record came from. A hand-entered attestation and one mirrored
      // from an induction actually completed on site are otherwise identical
      // rows, and the difference matters: only one has answers behind it.
      { key: "is_system_recorded", label: "Source", type: "inductionSource" },
    ],
    fields: [
      { name: "induction_type", label: "Induction Type", type: "text", required: true },
      { name: "worker_id", label: "Worker (leave blank for all)", type: "worker" },
      { name: "version", label: "Induction Version", type: "text" },
      { name: "assessment_score", label: "Assessment Score", type: "text" },
      { name: "completion_date", label: "Completion Date", type: "date" },
      { name: "expiry_date", label: "Expiry Date", type: "date" },
      { name: "signature_collected", label: "Digital Signature Collected", type: "switch" },
      { name: "document_attachment_id", label: "Signed Induction Record", type: "file" },
      { name: "notes", label: "Notes", type: "textarea" },
    ],
  },

  safety: {
    key: "safety",
    endpoint: "contractor-safety-document",
    title: "Safety Document Register",
    addLabel: "Upload Document",
    empty: "No safety documents recorded yet.",
    columns: [
      { key: "display_label", label: "Document", type: "text" },
      { key: "version", label: "Version", type: "text" },
      { key: "review_date", label: "Last Review", type: "date" },
      { key: "expiry_date", label: "Expiry", type: "expiry" },
    ],
    fields: [
      { name: "document_type", label: "Document Type", type: "select", options: SAFETY_DOC_TYPES, required: true },
      {
        name: "custom_name", label: "Document Name", type: "text",
        showWhen: (v) => v.document_type === "other",
      },
      { name: "version", label: "Document Version", type: "text" },
      { name: "review_date", label: "Last Review Date", type: "date" },
      { name: "expiry_date", label: "Expiry Date", type: "date" },
      { name: "document_attachment_id", label: "Document File", type: "file" },
      { name: "notes", label: "Notes", type: "textarea" },
    ],
  },
};

/** Maps expiry_status → pill class + label (shared with the list page CSS). */
export const EXPIRY_META = {
  active: { label: "Current", className: "expiry-active" },
  expiring: { label: "Expiring", className: "expiry-expiring" },
  expired: { label: "Expired", className: "expiry-expired" },
};

/** Generic status pill palette for worker/asset statuses. */
/**
 * What AirAgri access this person has, as reported by
 * contractor_workers.login_access.
 *
 * The five states answer TWO questions, which is why the wording matters: does an
 * account exist at all (No Account), and is that account a member of THIS property
 * (No Access / Pending / Active)?
 *
 * Kept apart from STATUS_PILL on purpose. Every other status on the contractor
 * profile is COMPLIANCE — induction, competency, site access, the contractor's
 * own approval state — and none of them grants anybody a login. Access is
 * granted only by accepting the contractor user — here, or under Property
 * Settings → User Access Control. A farm that approves a contractor and sees nothing but green pills
 * would otherwise reasonably assume their workers can sign in; these hints are
 * where that gets said.
 */
/**
 * The three Site Access states shown in the merged column.
 *
 * Approved means, and only means, that the person can sign in to this property
 * right now — it is derived from their actual access, not read back from a field
 * somebody set once. Rejected is a stored human decision that outranks
 * everything. Everything else is Pending.
 */
export const SITE_ACCESS_META = {
  approved: {
    label: "Approved",
    className: "pill-green",
    hint: "This person can sign in to this property and use AirAgri here.",
  },
  pending: {
    label: "Pending",
    className: "pill-amber",
    hint: "This person cannot sign in to this property yet.",
  },
  rejected: {
    label: "Rejected",
    className: "pill-red",
    hint:
      "This person has been refused access to this property. They cannot be " +
      "accepted until their Site Access is changed to Pending or Approved.",
  },
};

/**
 * WHY a row is Pending, from the underlying access detail. The pill says the
 * decision; this says what is actually in the way, which is usually what somebody
 * needs to know to act.
 */
export const SITE_ACCESS_REASON = {
  none: "No AirAgri account exists for this person's email address. Accepting them creates one and emails a temporary password.",
  no_access: "An AirAgri account exists for this email address, but it is not a member of this property.",
  pending: "They hold an invitation to this property that has not been accepted yet.",
  deactivated: "Their AirAgri account has been deactivated, so they cannot sign in anywhere. Reactivate it under Property Settings.",
  active: "They can sign in to this property.",
};

export const STATUS_PILL = {
  approved: "pill-green",
  complete: "pill-green",
  verified: "pill-green",
  current: "pill-green",
  conditional: "pill-amber",
  pending: "pill-blue",
  overdue: "pill-red",
  expired: "pill-red",
  rejected: "pill-red",
  failed: "pill-red",
};
